import React, { useState, useEffect, useRef } from 'react';

import ATHeader from './tableUtils/ATHeader';
import ATBody from './tableUtils/ATBody';
import { compareObjectValues } from '../../../../helpers/helpers';
import { usePracticeViewerState, useDataEventsDispatch } from '../../../../store/hooks/dataEvents';
import { useTablePropertiesState } from '../../../../store/hooks/tableProperties';
import { Player, AccordionTable as ATProps, ExpandedAcc } from '../types';
import { useParams } from 'react-router-dom';

const getSortableValueComparer = (orderColumn: number, unmutedPlayers: Array<Player>, sortDirection: number) => {
  switch (Math.abs(orderColumn)) {
    case 2:
      return unmutedPlayers.sort(compareObjectValues('name', undefined, sortDirection));
    case 3:
      return unmutedPlayers.sort(compareObjectValues('metrics', 'accelMed', sortDirection));
    case 4: 
      return unmutedPlayers.sort(compareObjectValues('metrics', 'accelFast', sortDirection));
    case 5:
      return unmutedPlayers.sort(compareObjectValues('metrics', 'decelMed', sortDirection));
    case 6:
      return unmutedPlayers.sort(compareObjectValues('metrics', 'decelFast', sortDirection));
    default:
      return unmutedPlayers;
  }
};

const sortPlayersOnOrderColumn = (players: Array<Player>, orderColumn: number) => {
  if (orderColumn) {
    const unmutedPlayers = [...players];
    const sortDirection = orderColumn > 0 ? -1 : 1;
    return getSortableValueComparer(orderColumn, unmutedPlayers, sortDirection);
  }
  return players;
};

const AccordionTable = ({ headers, isProcessing } : ATProps) => {

  const { id: practiceId } = useParams<{ id: string }>();
  const { metrics, players = [] } = usePracticeViewerState();
  const { pinnedRows, orderColumn } = useTablePropertiesState();
  const { getPlayersMetrics, setMetricCurrentSecond } = useDataEventsDispatch();

  const [expandedAcc, setExpandedAcc] = useState<ExpandedAcc>([]);
  const [flaggedPlayers, setFlaggedPlayers] = useState<Array<Player>>([]);
  const [nonFlaggedPlayers, setNonFlaggedPlayers] = useState<Array<Player>>([]);

  const theadRef = useRef<HTMLTableSectionElement | null>(null);
  const [theadHeight, setTheadHeight] = useState(0);

  useEffect(() => {
    if (!theadRef.current) return;
    const observer = new ResizeObserver(() => {
      setTheadHeight(theadRef.current!.offsetHeight);
    });
    observer.observe(theadRef.current);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    // any flag actions will dispatch the request
    if (pinnedRows.length) getPlayersMetrics(pinnedRows, practiceId, isProcessing);
  }, [pinnedRows]);

  useEffect(() => {
    const sortedPlayers = sortPlayersOnOrderColumn(players, orderColumn);
    setPlayersWithFlagsFirst(sortedPlayers);
  }, [players, metrics, pinnedRows, orderColumn]);

  const setPlayersWithFlagsFirst = (newPlayers: Array<Player>) => {
    if (pinnedRows && pinnedRows.length) {
      const flagPlayers: Player[] = [], nonFlagPlayers: Player[] = [];
      newPlayers.forEach(player => {
        if (pinnedRows.includes(player.playerId)) flagPlayers.push(player);
        else nonFlagPlayers.push(player);
      });
      setFlaggedPlayers(flagPlayers);
      setNonFlaggedPlayers(nonFlagPlayers);
    } else {
      setFlaggedPlayers([]);
      setNonFlaggedPlayers(newPlayers);
    }
  };

  return (
    <table className="nvd-custom-table table">
      <thead ref={theadRef}>
        <ATHeader 
          headers={headers} 
          sortedPlayers={flaggedPlayers.concat(nonFlaggedPlayers)}
          setExpandedAcc={setExpandedAcc}
        />
      </thead>

      <tbody className="accordion-container">
        {/* Flagged Content */}
        <ATBody
          metrics={metrics}
          practiceId={practiceId}
          className="flagged-content"
          expandedAcc={expandedAcc}
          sortedPlayers={flaggedPlayers}
          setExpandedAcc={setExpandedAcc}
          setMetricCurrentSecond={setMetricCurrentSecond}
          pinned={true}
          theadHeight={theadHeight}
        />

        {/* Non Flagged Content */}
        <ATBody sortedPlayers={nonFlaggedPlayers} /> 
      </tbody>
    </table>
  );
};

export default AccordionTable;